import React from "react";
import { Avatar } from "@campgladiator/cgui-core.atoms.avatar";
import { Label, Paragraph } from "@campgladiator/cgui-core.atoms.typography";
import { centsToUSD } from "@campgladiator/cg-common.utility.numbers";
import { getActiveProductPriceById } from "@campgladiator/cg-common.utility.prices";
import { UpgradeMembershipModalProps } from "../../UpgradeMembershipModal";
import { ProductHeading } from "../ProductHeading";
import { TermsCheckbox } from "../../../TermsCheckbox";
import styles from "./ConfirmUpgrade.module.scss";
import classNames from "classnames";
import { SelectedProductOption } from "../SelectProduct";
import {
  ProgramScheduleDTO,
  SwapType,
} from "@campgladiator/cg-common.types.types";
import {
  formatTime,
  getFormattedWeekDays,
} from "@campgladiator/cg-common.utility.dates";

export type ConfirmUpgradeProps = {
  onChangeTermsCheckbox: (isChecked: boolean) => void;
  onClickViewTerms: () => void;
  hasTrainerGrants: boolean;
  selectedProductOption: SelectedProductOption;
  selectedSchedule: ProgramScheduleDTO;
  trainerName: string;
  trainerPhotoUrl: string;
  swapType?: SwapType;
} & Pick<UpgradeMembershipModalProps, "productName" | "productPrice">;

const ConfirmUpgrade = ({
  productName,
  productPrice,
  selectedProductOption,
  selectedSchedule,
  trainerPhotoUrl,
  trainerName,
  onChangeTermsCheckbox,
  onClickViewTerms,
  hasTrainerGrants,
  swapType,
}: ConfirmUpgradeProps) => {
  const priceInCents =
    getActiveProductPriceById({
      prices: selectedProductOption?.productDetails?.prices,
      productId: selectedProductOption?.productDetails?.stripeProductId,
    })?.amount || 0;
  const price = centsToUSD(priceInCents);

  const daysOfTheWeek = selectedSchedule
    ? getFormattedWeekDays({
        daysOfTheWeek: selectedSchedule.daysOfTheWeek,
      })
    : undefined;
  const startTime = selectedSchedule
    ? formatTime(selectedSchedule.startTime)
    : undefined;

  return (
    <>
      <ProductHeading productName={productName} productPrice={productPrice} />
      <div className={styles.innerContent}>
        <article className={styles.upgradeSummary}>
          <section>
            <Label className={styles.label}>Upgrade To</Label>
            <div className={styles.details}>
              <Paragraph className={styles.name}>
                {selectedProductOption?.productDetails?.stripeProductName}
              </Paragraph>
              <Paragraph className={styles.price}>&nbsp;{price}/mo</Paragraph>
            </div>
          </section>

          {swapType && (
            <section>
              <Label className={styles.label}>Swap type</Label>
              <div className={styles.details}>
                <Paragraph className={styles.name}>{swapType}</Paragraph>
              </div>
            </section>
          )}

          <section>
            <Label className={styles.label}>Trainer</Label>
            <div className={classNames(styles.details, styles.trainerDetails)}>
              <Avatar src={trainerPhotoUrl} size="24px" />
              <Paragraph
                size="xsmall"
                weight="book"
                className={styles.trainerName}
              >
                {trainerName}
              </Paragraph>
            </div>
          </section>
          <section>
            <Label className={styles.label}>Schedule</Label>
            <div className={classNames(styles.details, styles.scheduleDetails)}>
              <Paragraph
                size="xsmall"
                weight="book"
                className={styles.trainerName}
              >
                {daysOfTheWeek} - {startTime}
              </Paragraph>
              <Paragraph
                size="xsmall"
                weight="book"
                className={styles.trainerName}
              >
                {selectedSchedule?.address?.name ?? ""}
              </Paragraph>
              <Paragraph
                size="xsmall"
                weight="book"
                className={styles.trainerName}
              >
                {selectedSchedule?.address?.address1 ?? ""}
                {selectedSchedule?.approvalRequired === "ONLINE" && "Online"}
              </Paragraph>
            </div>
          </section>
          <section>
            <Paragraph size="tiny" weight="book" className={styles.attention}>
              Attention: the new membership will start on the member’s next bill
              date
            </Paragraph>
          </section>
        </article>
        {!hasTrainerGrants && (
          <TermsCheckbox
            showTermsLink
            onClickViewTerms={onClickViewTerms}
            onChangeTermsCheckbox={onChangeTermsCheckbox}
            hasTrainerGrants={hasTrainerGrants}
          />
        )}
      </div>
    </>
  );
};

export default ConfirmUpgrade;
